import { $generateHtmlFromNodes } from "@lexical/html";
import { LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode } from "@lexical/rich-text";
import { Components, plugins } from "@thisgorilla/primate-ds";
import {
  DOMExportOutput,
  EditorState,
  Klass,
  LexicalEditor,
  LexicalNode,
} from "lexical";
import {
  createBeautifulMentionNode,
  CustomBeautifulMentionNode,
} from "lexical-beautiful-mentions";
import { useContext, useState } from "react";

import { ArticleContext } from "../../..";
import { API_CALLS } from "../../../../../../API/api_calls";
import { useAppSelector } from "../../../../../../redux";
import { ArticleProps } from "../../../../../../redux/slices/article/Article";
import { RICHTEXT_INITIAL_VALUE } from "../../../../../../utils";
import { FlexRowEnd } from "../../../../../Common/Styles";
import { useFindContent } from "../../../useFindContent";
import { ContentProps } from "../../Content";

import { useTheme } from "styled-components";
import instance from "../../../../../../axios/axios-user";
import { ContentEditableContainer, PlaceholderContainer } from "./style";
import {
  constructImportMap,
  convertHtmlToNode,
  editorTheme,
  exportMentionAsTemplate,
} from "./utils";

const { MentionPlugin, EmojiPlugin, MultipleEditorStorePlugin } = plugins;
const { MentionChip, MentionsPlugin } = MentionPlugin;
const { CustomUrlNode, UrlMetadataPlugin } = plugins.UrlMetadataPlugin;

const CustomEmojiPlugin = ({ id }: { id: string }) => {
  const { Color } = useTheme();
  const { editors } = useContext(ArticleContext);
  const editor = editors[id];

  return (
    <EmojiPlugin
      editor={editor}
      scrollAreaId={""}
      color={Color.icon["icon-tertiary"].value}
      position="static"
      size="Small"
    />
  );
};

const Editor = ({ id }: ContentProps.CommonProps) => {
  const articleId = `article-editor-${id}`;
  const { suggestedUsers } = useAppSelector((state) => state.me);
  const {
    content_data,
    post_id,
    content_id,
    content_type,
    row_config,
    topic_meta,
    article_order,
  } = useFindContent(id) as ArticleProps.WithTextContent;
  const { Summary } = content_data;
  const [value, setValue] = useState(
    Summary ? Summary : RICHTEXT_INITIAL_VALUE
  );

  const { setFocusedEditor, setFloatingAnchorElem, focusedEditor, setEditors } =
    useContext(ArticleContext);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  const MentionNode = createBeautifulMentionNode((mentionComponent) => (
    <MentionChip {...mentionComponent} />
  ));

  const initialConfig = {
    namespace: "MyEditor",
    editorState: (editor: LexicalEditor) => convertHtmlToNode(editor, value),
    nodes: [
      ListItemNode,
      ListNode,
      LinkNode,
      CustomUrlNode,
      HeadingNode,
      HorizontalRuleNode,
      ...MentionNode,
    ],
    theme: editorTheme,
    onError(error: Error) {
      console.log(error);
    },
    html: {
      export: new Map<
        Klass<LexicalNode | any>,
        (editor: LexicalEditor, target: LexicalNode) => DOMExportOutput
      >([[CustomBeautifulMentionNode, exportMentionAsTemplate]]),
      import: constructImportMap(
        topic_meta?.content_users ?? [],
        topic_meta?.urls ?? []
      ),
    },
  };

  const onChange = (editorState: EditorState, editor: LexicalEditor) => {
    editorState.read(() => {
      const html = $generateHtmlFromNodes(editor);
      setValue(html);
    });
  };
  return (
    <LexicalComposer initialConfig={initialConfig as any}>
      <RichTextPlugin
        contentEditable={
          <div ref={focusedEditor === articleId ? onRef : null}>
            <ContentEditableContainer
              id={articleId}
              onFocus={() => {
                setFocusedEditor(articleId);
              }}
              onBlur={() => {
                if (
                  (!Summary && value === RICHTEXT_INITIAL_VALUE) ||
                  value === Summary
                )
                  return;
                API_CALLS.ARTICLE.createOrUpdateArticleContent({
                  post_id,
                  content_type,
                  content_id,
                  element_role: row_config.element_role,
                  article_order,
                  content_data: JSON.stringify({
                    summary: value,
                    blueprint: value,
                  }),
                });
              }}
            />
          </div>
        }
        placeholder={
          <PlaceholderContainer>
            Start writing... You can include @mentions.
          </PlaceholderContainer>
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      <FlexRowEnd>
        <CustomEmojiPlugin id={articleId} />
        <Components.IconButton
          iconType="Trash2"
          size="Small"
          disabled={false}
          kind="Tertiary"
          onClick={() => {
            API_CALLS.ARTICLE.deleteASingleArticleContent(id);
          }}
        />
      </FlexRowEnd>
      <MultipleEditorStorePlugin id={articleId} setEditors={setEditors} />
      <ListPlugin />
      <HistoryPlugin />
      <OnChangePlugin onChange={onChange} />
      <MentionsPlugin mentionList={suggestedUsers} />
      <UrlMetadataPlugin axiosInstance={instance} />
      <HorizontalRulePlugin />
      {/* {floatingAnchorElem && articleId === focusedEditor && (
        <FloatingTextFormatToolbar anchorElem={floatingAnchorElem} />
      )} */}
    </LexicalComposer>
  );
};

export default Editor;
